import { useEffect, useState } from 'react'
import { useI18n } from '../../i18n/I18nProvider'
import { useClipboard } from '../../hooks/useClipboard'
import { entropyBits } from '../../crypto/render'
import type { Policy } from '../../crypto/spec'
import { Button, IconButton, cx } from '../ui/primitives'
import { CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, LinkIcon } from '../ui/icons'

interface ResultPanelProps {
  password: string
  elapsedMs: number
  policy: Policy
  stale: boolean
  recipeUrl: string
}

type Strength = 'weak' | 'fair' | 'good' | 'excellent'

/** Faixas usuais para entropia de senha; servem como sinal, não como promessa. */
function strengthOf(bits: number): Strength {
  if (bits < 60) return 'weak'
  if (bits < 80) return 'fair'
  if (bits < 110) return 'good'
  return 'excellent'
}

const STRENGTH_STYLES: Record<Strength, { bar: string; text: string; fill: number }> = {
  weak: { bar: 'bg-danger', text: 'text-danger', fill: 25 },
  fair: { bar: 'bg-warn', text: 'text-warn', fill: 50 },
  good: { bar: 'bg-ok', text: 'text-ok', fill: 75 },
  excellent: { bar: 'accent-gradient', text: 'text-accent', fill: 100 },
}

/** Colorir por classe torna a leitura manual da senha muito menos penosa. */
function classOf(char: string): string {
  if (/[0-9]/.test(char)) return 'text-accent-2'
  if (/[a-zA-Z]/.test(char)) return 'text-fg'
  return 'text-accent'
}

export function ResultPanel({
  password,
  elapsedMs,
  policy,
  stale,
  recipeUrl,
}: ResultPanelProps) {
  const { t } = useI18n()
  const [revealed, setRevealed] = useState(false)
  const passwordClipboard = useClipboard({ autoClear: true })
  const recipeClipboard = useClipboard()

  // Uma senha nova sempre começa oculta, mesmo que a anterior estivesse visível.
  useEffect(() => setRevealed(false), [password])

  const bits = entropyBits(policy)
  const strength = strengthOf(bits)
  const style = STRENGTH_STYLES[strength]

  return (
    <section
      className={cx(
        'animate-rise rounded-2xl border p-4 transition',
        stale ? 'border-warn/50 bg-warn/5' : 'border-accent/30 bg-accent-soft/40',
      )}
      aria-live="polite"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-fg">{t.gen.result.title}</h3>
        <span className="text-xs text-fg-subtle tabular-nums">
          {t.gen.result.elapsed(elapsedMs)}
        </span>
      </div>

      <div className="flex items-stretch gap-2">
        <output
          className={cx(
            'flex min-h-14 flex-1 items-center overflow-x-auto rounded-xl border border-border bg-surface px-3 py-2',
            'font-mono text-lg leading-snug font-medium tracking-wide break-all',
          )}
        >
          {revealed ? (
            <span>
              {[...password].map((char, index) => (
                <span key={index} className={classOf(char)}>
                  {char === ' ' ? '␣' : char}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-fg-subtle select-none" aria-label={t.gen.result.reveal}>
              {'•'.repeat(Math.min(password.length, 40))}
            </span>
          )}
        </output>
        <IconButton
          label={revealed ? t.gen.result.hide : t.gen.result.reveal}
          onClick={() => setRevealed((value) => !value)}
          className="self-center border border-border bg-surface"
        >
          {revealed ? <EyeOffIcon className="size-5" /> : <EyeIcon className="size-5" />}
        </IconButton>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-32 flex-1 items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2">
            <div
              className={cx('h-full rounded-full transition-all', style.bar)}
              style={{ width: `${style.fill}%` }}
            />
          </div>
          <span className={cx('text-xs font-medium', style.text)}>
            {t.gen.result.strength[strength]}
          </span>
        </div>
        <span className="text-xs text-fg-subtle tabular-nums">
          {t.gen.result.entropy(bits)}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="primary"
          className="flex-1"
          onClick={() => void passwordClipboard.copy(password)}
        >
          {passwordClipboard.state === 'copied' ? (
            <>
              <CheckIcon className="size-4" />
              {t.gen.result.copied}
            </>
          ) : (
            <>
              <CopyIcon className="size-4" />
              {t.gen.result.copy}
            </>
          )}
        </Button>
        <Button
          variant="secondary"
          onClick={() => void recipeClipboard.copy(recipeUrl)}
          // O rótulo some em telas estreitas, então o nome acessível precisa
          // vir do aria-label — senão o botão vira um ícone anônimo.
          aria-label={t.gen.result.recipe}
          title={t.gen.result.recipeNote}
        >
          <LinkIcon className="size-4" />
          <span className="hidden sm:inline">
            {recipeClipboard.state === 'copied'
              ? t.gen.result.recipeCopied
              : t.gen.result.recipe}
          </span>
        </Button>
      </div>

      <p className="mt-2 text-xs text-fg-subtle">
        {passwordClipboard.state === 'failed'
          ? t.gen.result.copyFailed
          : t.gen.result.clipboardNote}
      </p>

      {recipeClipboard.state === 'copied' && (
        <p className="mt-1 text-xs text-warn">{t.gen.result.recipeNote}</p>
      )}

      {stale && <p className="mt-2 text-xs font-medium text-warn">{t.gen.result.stale}</p>}
    </section>
  )
}
